/**
 * RecallPage — the lot → Resultat recall worklist. A conservative two-step: PREVIEW (dry-run, no write)
 * enumerates the valide/corrige results stamped with a lot, then CONFIRM flips the lot → quarantaine and
 * builds the biologiste worklist. Result VALUES are never auto-mutated — the biologiste decides.
 *
 * Lands either cold (pick a reagent → a lot → preview) or pre-seeded from the catalogue
 * (location.state = { reactifId, lotId?, numeroLot? }). Full 5-state DoD off the reagent catalog.
 */
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  ForbiddenState,
  FormField,
  LoadingState,
  OfflineState,
  Select,
  Spinner,
} from "@/ui";
import { useCan } from "@/auth";
import { useT, useUiStore } from "@/app";
import { ApiError } from "@/api";
import { INV_CAPS } from "../caps";
import { previewRecall, useLabId, useLots, useReactifs } from "../api";
import type { RecallPreview } from "../types";
import { idStr } from "../types";
import { reactifOptions } from "../lib";
import { PageHeader } from "../components/PageHeader";
import { ConfirmRecallModal } from "../components/ConfirmRecallModal";

interface RecallHandoff {
  reactifId?: string;
  lotId?: string;
  numeroLot?: string;
}

export function RecallPage() {
  const { t } = useT();
  const labId = useLabId();
  const location = useLocation();
  const online = useUiStore((s) => s.online);
  const canRead = useCan(INV_CAPS.reactifRead);
  const canRecall = useCan(INV_CAPS.recallRun);

  const handoff = (location.state ?? null) as RecallHandoff | null;

  const catalogQuery = useReactifs(labId, canRead && online);
  const reactifs = useMemo(() => catalogQuery.data ?? [], [catalogQuery.data]);
  const options = useMemo(() => reactifOptions(reactifs), [reactifs]);

  const [reactifId, setReactifId] = useState(handoff?.reactifId ?? "");
  const [lotId, setLotId] = useState(handoff?.lotId ?? "");
  const [preview, setPreview] = useState<RecallPreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // NOVA-027 — list lots across ALL lifecycle states (incl. perime/epuise/quarantaine) so a Magasinier
  // can recall an expired/exhausted lot, not just an active one. Needs inventory.lot.read.
  const canReadLots = useCan(INV_CAPS.lotRead);
  const lotsQuery = useLots(labId, reactifId || null, canReadLots && online && reactifId !== "");
  const lots = useMemo(() => lotsQuery.data ?? [], [lotsQuery.data]);

  // Reset the preview whenever the lot selection changes.
  useEffect(() => {
    setPreview(null);
    setPreviewError(null);
  }, [lotId]);

  // Reset the lot when the reagent changes (unless seeded from a handoff for this same reagent).
  useEffect(() => {
    setLotId((cur) => (handoff?.reactifId === reactifId && handoff?.lotId ? handoff.lotId : cur === handoff?.lotId ? cur : ""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reactifId]);

  const runPreview = () => {
    if (!lotId || !canRecall) return;
    setPreviewing(true);
    setPreviewError(null);
    previewRecall(lotId)
      .then((res) => setPreview(res))
      .catch((err: unknown) => setPreviewError(err instanceof ApiError ? err.message : String(err)))
      .finally(() => setPreviewing(false));
  };

  const lotOptions = lots.map((l) => ({
    value: idStr(l.lot_id),
    label: `${l.numero_lot} · ${t(`inventory.lots.statut.${l.statut}`)}`,
  }));

  const header = <PageHeader title={t("inventory.recall.title")} subtitle={t("inventory.recall.subtitle")} />;

  if (!canRecall) return <ForbiddenState requiredCapabilities={[INV_CAPS.recallRun]} />;
  if (!online) {
    return (
      <Screen>
        {header}
        <OfflineState onRetry={() => void catalogQuery.refetch()} cached={reactifs.length > 0} />
      </Screen>
    );
  }
  if (catalogQuery.isLoading) {
    return (
      <Screen>
        {header}
        <LoadingState variant="table" rows={4} />
      </Screen>
    );
  }
  if (catalogQuery.isError) {
    return (
      <Screen>
        {header}
        <ErrorState
          message={catalogQuery.error.message}
          code={catalogQuery.error.code}
          onRetry={() => void catalogQuery.refetch()}
        />
      </Screen>
    );
  }

  return (
    <Screen>
      {header}

      <Card title={t("inventory.recall.lot")}>
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-4)" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--nv-s-4)" }}>
            <FormField label={t("inventory.reactif.pick")}>
              {(p) => (
                <Select
                  {...p}
                  value={reactifId}
                  placeholder={t("inventory.reactif.pick.placeholder")}
                  options={options}
                  onChange={(e) => setReactifId(e.target.value)}
                />
              )}
            </FormField>
            <FormField label={t("inventory.recall.lot")}>
              {(p) =>
                lotsQuery.isLoading ? (
                  <div style={{ display: "flex", alignItems: "center", gap: "var(--nv-s-2)", minHeight: "var(--nv-min-touch)" }}>
                    <Spinner /> <span style={{ color: "var(--nv-slate)" }}>{t("inventory.reorder.loading")}</span>
                  </div>
                ) : (
                  <Select
                    {...p}
                    value={lotId}
                    disabled={reactifId === "" || lots.length === 0}
                    placeholder={t("inventory.recall.lot.placeholder")}
                    options={lotOptions}
                    onChange={(e) => setLotId(e.target.value)}
                  />
                )
              }
            </FormField>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button variant="secondary" loading={previewing} disabled={!lotId} onClick={runPreview}>
              {t("inventory.recall.preview.cta")}
            </Button>
          </div>
        </div>
      </Card>

      {previewError && (
        <ErrorState title={t("inventory.recall.preview.title")} message={previewError} onRetry={runPreview} />
      )}

      {preview && !previewError && (
        <Card
          title={t("inventory.recall.preview.title")}
          actions={
            <Badge tone={preview.statut === "quarantaine" ? "warn" : "info"} glyph={preview.statut === "quarantaine" ? "❚❚" : "●"}>
              {t("inventory.recall.preview.statut")}: {preview.statut}
            </Badge>
          }
        >
          {preview.affected_count === 0 ? (
            <EmptyState glyph="●" title={t("inventory.recall.preview.title")} description={t("inventory.recall.preview.none")} />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-4)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "var(--nv-s-3)", flexWrap: "wrap" }}>
                <Badge tone="danger" glyph="◆">
                  {t("inventory.recall.preview.count", { n: preview.affected_count })}
                </Badge>
                <Button variant="danger" disabled={!canRecall} onClick={() => setConfirmOpen(true)}>
                  {t("inventory.recall.confirm.cta")}
                </Button>
              </div>

              <div>
                <div style={{ fontSize: "var(--nv-fs-label)", color: "var(--nv-slate)", marginBottom: "var(--nv-s-2)" }}>
                  {t("inventory.recall.resultats.title")}
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--nv-s-2)" }}>
                  {preview.resultat_ids.map((rid) => (
                    <span key={rid} className="nv-mono nv-ltr-island" style={{
                      padding: "var(--nv-s-1) var(--nv-s-2)",
                      borderRadius: "var(--nv-r-sm)",
                      background: "var(--nv-surface-2)",
                      border: "1px solid var(--nv-line-faint)",
                      fontSize: "var(--nv-fs-caption)",
                    }}>
                      {rid}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </Card>
      )}

      <ConfirmRecallModal
        open={confirmOpen}
        lotId={lotId || null}
        reactifId={reactifId || null}
        affectedCount={preview?.affected_count ?? 0}
        onClose={() => setConfirmOpen(false)}
        onConfirmed={() => {
          setConfirmOpen(false);
          // Re-preview so the statut chip reflects the new quarantaine state.
          runPreview();
        }}
      />
    </Screen>
  );
}

function Screen({ children }: { children: React.ReactNode }) {
  return <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-5)" }}>{children}</div>;
}
